import type { UseFormReturn } from "react-hook-form";
import { Controller as RHFController } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Combobox, Switch } from "../../../../../../components/ui";
import type { ThemeClasses, RoutePoint, RouteFormValues } from "../types";
import { LINEAR_BUFFER_DEFAULT, LINEAR_BUFFER_MIN, LINEAR_BUFFER_MAX } from "../types";
import { RouteCoordinatesForm } from "./forms/RouteCoordinatesForm";
import { useStatusesStore } from "../../../../../../store/admin/statusesStore";
import { getStatusOptionsForEntity } from "../../../../../../utils/getStatusEntity";

interface RouteFormProps {
  form: UseFormReturn<RouteFormValues>;
  isEdit: boolean;
  loadingSubmit: boolean;
  hasRealChanges: () => boolean;
  themeClasses: ThemeClasses;
  isDarkMode: boolean;
  routePoints: RoutePoint[];
  onCoordinateEdit: (index: number, field: "latitude" | "longitude", raw: string) => void;
  onAddressEdit: (index: number, rawAddress: string) => void;
  onAddPoint: () => void;
  onRemovePoint: (index: number) => void;
  onCorridorBufferChange: (value: number) => void;
  onSubmit: (data: RouteFormValues) => void;
  onCancel: () => void;
}

export function RouteForm({
  form,
  isEdit,
  loadingSubmit,
  hasRealChanges,
  themeClasses,
  isDarkMode,
  routePoints,
  onCoordinateEdit,
  onAddressEdit,
  onAddPoint,
  onRemovePoint,
  onCorridorBufferChange,
  onSubmit,
  onCancel,
}: RouteFormProps) {
  const { t } = useTranslation("adminSections");
  const {
    register,
    control,
    watch,
    setValue,
    handleSubmit,
    formState: { errors, isValid },
  } = form;

  const statuses = useStatusesStore((state) => state.statuses);
  const statusOptionsSelect = getStatusOptionsForEntity(statuses, "routes", t);

  const inputClassName = `w-full min-h-10 px-3 py-2 text-xs bg-app border ${themeClasses.cardBorder} rounded-lg ${themeClasses.text}`;

  const isSubmitDisabled =
    !isValid ||
    (isEdit && !hasRealChanges()) ||
    loadingSubmit ||
    routePoints.length < 2;

  return (
    <form
      className="flex flex-col w-full gap-5 overflow-y-auto max-h-[75vh] pr-2"
      onSubmit={handleSubmit(onSubmit)}
    >
      {/* ── Basic info ─────────────────────────────────────────────────────── */}
      <section className="flex flex-col gap-4">
        {/* Route name */}
        <div className="flex flex-col gap-1.5">
          <label className={`text-sm font-medium ${themeClasses.text}`}>
            Nombre de la ruta <span className="text-error">*</span>
          </label>
          <input
            {...register("name", { required: true })}
            type="text"
            className={`${inputClassName} ${
              errors.name
                ? "focus:outline-none ring-2 ring-error"
                : "focus:outline-none focus:ring-2 focus:ring-primary-500"
            }`}
            placeholder="Ej: Ruta Logística Zona Norte"
          />
          {errors.name && (
            <span className="text-error text-xs">
              Este campo es obligatorio
            </span>
          )}
        </div>

        {/* Status */}
        <div className="flex flex-col gap-1.5">
          <label className={`text-sm font-medium ${themeClasses.text}`}>
            {t("routes.fields.status")} <span className="text-error">*</span>
          </label>
          <RHFController
            name="status_id"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <Combobox
                {...field}
                options={statusOptionsSelect}
                placeholder={t("routes.form.selectStatus")}
                value={statusOptionsSelect.find(
                  (opt) => opt.value === field.value
                )}
                onChange={(option: any) => field.onChange(option?.value)}
                isSearchable
                noOptionsMessage={() => t("routes.form.noStatusesAvailable")}
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            )}
          />
          {errors.status_id && (
            <span className="text-error text-xs">
              {t("routes.form.requiredField")}
            </span>
          )}
        </div>

        {/* Description */}
        <div className="flex flex-col gap-1.5">
          <label className={`text-sm font-medium ${themeClasses.text}`}>
            Descripción
          </label>
          <textarea
            {...register("description")}
            className={`${inputClassName} focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none`}
            rows={2}
            placeholder="Propósito de la ruta, horarios, observaciones..."
          />
        </div>

        {/* Deviation Alerts Toggle */}
        <div className="flex items-center justify-between gap-3">
          <div className="flex flex-col gap-0.5">
            <label className={`text-sm font-medium ${themeClasses.text}`}>
              Alertas de desvío
            </label>
            <span className={`text-xs ${themeClasses.textSecondary}`}>
              Activa el monitoreo de desvío de ruta y notificaciones en tiempo real
            </span>
          </div>
          <RHFController
            name="deviationAlertsEnabled"
            control={control}
            render={({ field }) => (
              <Switch checked={!!field.value} onChange={field.onChange} />
            )}
          />
        </div>
      </section>

      <hr className={`${themeClasses.border}`} />

      {/* Hidden inputs to keep react-hook-form validation for radii */}
      <input type="hidden" {...register("first_point_radius", { required: true, min: 50, max: 10000 })} />
      <input type="hidden" {...register("last_point_radius", { required: true, min: 50, max: 10000 })} />
      <input type="hidden" {...register("principal_route_buffer", { required: true, min: LINEAR_BUFFER_MIN, max: LINEAR_BUFFER_MAX })} />

      {/* ── Route points ───────────────────────────────────────────────────── */}
      <RouteCoordinatesForm
        routePoints={routePoints}
        themeClasses={themeClasses}
        isDarkMode={isDarkMode}
        onCoordinateEdit={onCoordinateEdit}
        onAddressEdit={onAddressEdit}
        onAddPoint={onAddPoint}
        onRemovePoint={onRemovePoint}
        firstPointRadius={watch("first_point_radius") || 0}
        lastPointRadius={watch("last_point_radius") || 0}
        onFirstPointRadiusChange={(v) => setValue("first_point_radius", v, { shouldValidate: true })}
        onLastPointRadiusChange={(v) => setValue("last_point_radius", v, { shouldValidate: true })}
        corridorBuffer={watch("principal_route_buffer") || LINEAR_BUFFER_DEFAULT}
        onCorridorBufferChange={onCorridorBufferChange}
        radiusErrors={{
          first: !!errors.first_point_radius,
          last: !!errors.last_point_radius,
        }}
      />

      {/* ── Actions ────────────────────────────────────────────────────────── */}
      <div className="flex gap-3 pt-2">
        <button
          type="button"
          onClick={onCancel}
          className={`flex-1 h-10 px-4 text-sm rounded-full ${themeClasses.buttonHover} border ${themeClasses.cardBorder} ${themeClasses.buttonText} font-medium transition-colors inline-flex items-center justify-center gap-1.5`}
        >
          Cancelar
        </button>
        <button
          type="submit"
          disabled={isSubmitDisabled}
          className={`flex-1 h-10 px-4 text-sm rounded-full bg-primary-500 hover:bg-primary-400 text-on-primary font-medium transition-colors inline-flex items-center justify-center gap-1.5 ${
            isSubmitDisabled ? "opacity-50 cursor-not-allowed" : ""
          }`}
        >
          {loadingSubmit
            ? isEdit
              ? "Guardando..."
              : "Creando..."
            : isEdit
            ? "Guardar Cambios"
            : "Crear Ruta"}
        </button>
      </div>
    </form>
  );
}
